import journeyMap from "../../UserJourney/UserJourneyMap";
import CoreUtils from "../../core/coreUtils";
import InvalidArgumentError from "../../Errors/InvalidArgumentError";
import moment from "moment";

const totalRiskLevel = 4;

const ranges = {
    low: {
        key: "low",
        label: "Low",
        range: "0-10"
    },
    moderate: {
        key: "moderate",
        label: "Moderate",
        range: "11-15"
    },
    high: {
        key: "high",
        label: "High",
        range: "16-30"
    },
    veryHigh: {
        key: "veryHigh",
        label: "Very High",
        range: "31+"
    }
};

const UKDiabetesGuidelineDefinition = [
    {
        min: 0.305,
        max: 1,
        key: "veryHigh",
        label: "Very High",
        rangeInfo: "31+",
        riskLevel: 4 / (totalRiskLevel + 1),
        ranges: ranges
    },
    {
        min: 0.155,
        max: 0.305,
        key: "high",
        label: "High",
        rangeInfo: "16-30",
        riskLevel: 3 / (totalRiskLevel + 1),
        ranges: ranges
    },
    {
        min: 0.105,
        max: 0.155,
        key: "moderate",
        label: "Moderate",
        rangeInfo: "11-15",
        ranges: ranges,
        riskLevel: 2 / (totalRiskLevel + 1)
    },
    {
        min: 0,
        max: 0.105,
        key: "low",
        label: "Low",
        rangeInfo: "0-10",
        ranges: ranges,
        riskLevel: 1 / (totalRiskLevel + 1)
    }
];

export default class UKDiabetesRisk {
    constructor(answers) {
        this.score = UKDiabetesRisk.calcScore(answers);
        this.guideline = UKDiabetesRisk.getGuideLine(this.score);
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get RiskName() {
        return "UK_DIABETES";
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get GuidelineName() {
        return "UK_DRC_DIABETES";
    }

    isLow() {
        return this.guideline.key === UKDiabetesGuidelineDefinition[3].key;
    }

    isModerate() {
        return this.guideline.key === UKDiabetesGuidelineDefinition[2].key;
    }

    isHigh() {
        return this.guideline.key === UKDiabetesGuidelineDefinition[1].key;
    }

    isVeryHigh() {
        return this.guideline.key === UKDiabetesGuidelineDefinition[0].key;
    }

    /**
     * Calculates the overall score.
     * @param answers
     * @returns {*}
     */
    static calcScore(answers) {
        this.checkAnswers(answers);

        // Extract raw answer values
        let dob = answers[journeyMap.questionCodes.dateOfBirth];
        let age = moment().diff(dob, "years", false);
        let genderRaw = answers[journeyMap.questionCodes.gender];
        let ethnicityRaw = answers[journeyMap.questionCodes.ukEthnicityBasic];
        let bpMedRaw = answers[journeyMap.questionCodes.isTakingHighBloodPressureMed];
        let diabetesHistoryRaw = answers[journeyMap.questionCodes.hasDiabetesFamilyHistory];
        let bmi = answers[journeyMap.questionCodes.bmi];

        // Convert to calculation factors
        let sex = genderRaw === "Male" ? 1 : 0;
        let eth = ethnicityRaw === "White" ? 0 : 1;
        let antihyp = bpMedRaw === "Yes" ? 1 : 0;
        let familyhx = diabetesHistoryRaw === "Yes" ? 1 : 0;

        const score =
            0.0408359 * age +
            0.1839942 * sex +
            0.0820698 * bmi +
            0.7565977 * eth +
            0.5498978 * antihyp +
            0.4770517 * familyhx;

        const finalScore = 1 / (1 + Math.exp(-(-6.776772 + score)));

        return finalScore;
    }

    /**
     * Creates an invalid argument exception for this class.
     * @param name
     * @returns {InvalidArgumentError}
     */
    static invalidArgument(name) {
        return new InvalidArgumentError(`Diabetes Scoring: Not a valid ${name}`);
    }

    /**
     * Gets the UK Risk guideline for the given score.
     * @param score
     * @returns {*}
     */
    static getGuideLine(score) {
        return UKDiabetesGuidelineDefinition.find(gl => CoreUtils.isWithin(score, gl.min, gl.max));
    }

    /**
     * Checks all the required answers are present.
     * @param answers
     */
    static checkAnswers(answers) {
        if (!(journeyMap.questionCodes.dateOfBirth in answers)) {
            throw this.invalidArgument("DoB");
        }
        if (!(journeyMap.questionCodes.gender in answers)) {
            throw this.invalidArgument("gender");
        }
        if (!(journeyMap.questionCodes.ukEthnicityBasic in answers)) {
            throw this.invalidArgument("ethnicity");
        }
        if (!(journeyMap.questionCodes.hasDiabetesFamilyHistory in answers)) {
            throw this.invalidArgument("diabetes history");
        }
        if (!(journeyMap.questionCodes.isTakingHighBloodPressureMed in answers)) {
            throw this.invalidArgument("blood pressure medication");
        }
        if (!(journeyMap.questionCodes.bmi in answers)) {
            throw this.invalidArgument("bmi");
        }
    }
}

export { UKDiabetesGuidelineDefinition };
